/**
 * ZeroScript Service Contracts
 *
 * Zod schemas for ZeroScript (CHC0 casino automation) inter-service communication.
 * Backend ↔ ZeroScript Service (Port 57391)
 *
 * Source of truth:
 * - services/zeroscript/src/server.ts (endpoint definitions)
 * - services/zeroscript/src/client.ts (CHC0Client result types)
 * - backend/src/services/zeroscript.ts (ZeroScriptService wrapper)
 */
import { z } from 'zod';
// =============================================================================
// COMMON REQUEST/RESPONSE FIELDS
// =============================================================================
/**
 * Phone number is actually the user's Telegram ID as string
 * ZeroScript uses this as the account identifier in CHC0
 */
export const phoneNumberSchema = z.string().min(1, 'Phone number is required');
/**
 * Screenshot base64 - always null for HTTP client (legacy field from Puppeteer era)
 */
export const screenshotBase64Schema = z.string().nullable().optional();
// =============================================================================
// CHECK ACCOUNT
// POST /check-account
// =============================================================================
export const checkAccountRequestSchema = z.object({
    phoneNumber: phoneNumberSchema,
});
export const checkAccountResponseSchema = z.object({
    success: z.boolean(),
    exists: z.boolean(),
    accountCode: z.string().nullable(),
    screenshotBase64: screenshotBase64Schema,
    error: z.string().optional(),
});
// =============================================================================
// CHECK ACCOUNT BALANCE
// POST /check-account-balance
// =============================================================================
export const checkBalanceRequestSchema = z.object({
    phoneNumber: phoneNumberSchema,
});
/**
 * Balance is returned as string from ZeroScript (backend converts to integer)
 */
export const checkBalanceResponseSchema = z.object({
    success: z.boolean(),
    exists: z.boolean(),
    balance: z.string().nullable(), // String representation, e.g., "125.50"
    screenshotBase64: screenshotBase64Schema,
    error: z.string().optional(),
});
// =============================================================================
// CREATE ACCOUNT
// POST /create-account
// =============================================================================
export const createAccountRequestSchema = z.object({
    phoneNumber: phoneNumberSchema,
});
export const createAccountResponseSchema = z.object({
    success: z.boolean(),
    exists: z.boolean(), // true if account already existed
    created: z.boolean().optional(), // true if newly created
    accountCode: z.string().optional(),
    screenshotBase64: screenshotBase64Schema,
    error: z.string().optional(),
});
// =============================================================================
// CHANGE ACCOUNT BALANCE
// POST /change-account-balance
// Positive amount = deposit, Negative amount = withdrawal
// =============================================================================
export const changeBalanceRequestSchema = z.object({
    phoneNumber: phoneNumberSchema,
    amount: z.number(), // Positive = add, negative = subtract
});
export const changeBalanceResponseSchema = z.object({
    success: z.boolean(),
    exists: z.boolean().optional(),
    newBalance: z.string().nullable().optional(), // String representation after change
    error: z.string().nullable().optional(),
    screenshotBase64: screenshotBase64Schema,
});
// =============================================================================
// ZERO BALANCE (Withdraw All)
// POST /zero-balance
// =============================================================================
export const zeroBalanceRequestSchema = z.object({
    phoneNumber: phoneNumberSchema,
});
export const zeroBalanceResponseSchema = z.object({
    success: z.boolean(),
    exists: z.boolean(),
    previousBalance: z.string().nullable().optional(),
    withdrawn: z.string().nullable().optional(), // Amount that was withdrawn
    error: z.string().nullable().optional(),
});
// =============================================================================
// CLOSE ACCOUNT
// POST /close-account
// =============================================================================
export const closeAccountRequestSchema = z.object({
    phoneNumber: phoneNumberSchema,
});
export const closeAccountResponseSchema = z.object({
    success: z.boolean(),
    exists: z.boolean(),
    closed: z.boolean().optional(), // true if account was closed
    error: z.string().nullable().optional(),
});
// =============================================================================
// HEALTH CHECK
// GET /health
// =============================================================================
export const healthCheckResponseSchema = z.object({
    status: z.literal('ok'),
    service: z.literal('zeroscript'),
    technology: z.string(),
    version: z.string(),
});
// =============================================================================
// BACKEND SERVICE WRAPPER TYPES
// These are the shapes returned by backend/src/services/zeroscript.ts
// (processed versions of the raw ZeroScript responses)
// =============================================================================
/**
 * Result from ZeroScriptService.createAccount()
 */
export const createAccountResultSchema = z.object({
    success: z.boolean(),
    created: z.boolean(),
    exists: z.boolean(),
    error: z.string().optional(),
});
/**
 * Result from ZeroScriptService.checkAccount()
 */
export const checkAccountResultSchema = z.object({
    success: z.boolean(),
    exists: z.boolean(),
    accountCode: z.string().nullable(),
});
/**
 * Result from ZeroScriptService.checkBalance()
 * Balance is converted to integer
 */
export const checkBalanceResultSchema = z.object({
    success: z.boolean(),
    balance: z.number(), // Integer (floored from CHC0 decimal)
    error: z.string().optional(),
});
/**
 * Result from ZeroScriptService.changeBalance()
 */
export const changeBalanceResultSchema = z.object({
    success: z.boolean(),
    newBalance: z.number().nullable(),
    changedAmount: z.number(),
    error: z.string().optional(),
});
/**
 * Result from ZeroScriptService.resetBalance() (zeroBalance)
 */
export const resetBalanceResultSchema = z.object({
    success: z.boolean(),
    resetAmount: z.number(), // Integer amount that was reset
    error: z.string().optional(),
});
/**
 * Result from ZeroScriptService.closeAccount()
 */
export const closeAccountResultSchema = z.object({
    success: z.boolean(),
    closed: z.boolean(),
    error: z.string().optional(),
});
/**
 * Result from ZeroScriptService.recreateAccount()
 * Composite operation: zeroBalance → closeAccount → createAccount → checkAccount
 */
export const recreateAccountResultSchema = z.object({
    success: z.boolean(),
    accountCode: z.string().nullable(),
    resetAmount: z.number(),
    error: z.string().optional(),
});
//# sourceMappingURL=zeroscript.js.map